import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_PINNED_COLUMN_WIDTH = 150;

interface PinnedColumnStyleOptions {
  /** z-index applied to a pinned cell (headers typically sit above body cells). */
  readonly zIndex?: number;
  /** position used when the column is not pinned (headers use 'relative'). */
  readonly unpinnedPosition?: CSSProperties['position'];
  /**
   * Actual rendered column widths (keyed by column id), used to compute the
   * cumulative sticky offset of preceding pinned columns. When omitted the
   * offset falls back to `columnWidths` and then to a fixed default width.
   */
  readonly measuredWidths?: Record<string, number>;
  /**
   * Visible column ids in left-to-right display order. Used to order pinned
   * columns by their on-screen position rather than the order they were pinned
   * in — essential for the right side, where offsets must accumulate leftward
   * from the rightmost column. When omitted, the pinned arrays' own order is
   * used as a fallback.
   */
  readonly columnOrder?: string[];
}

/**
 * Cumulative sticky offset for a pinned column, in pixels.
 *
 * Pinned columns are laid out in visible column order (`columnOrder`). A
 * left-pinned column is pushed in by the widths of the pinned columns to its
 * left; a right-pinned column is pushed in by the widths of the pinned columns
 * to its right (so the rightmost pinned column sits flush at right: 0). Using
 * real widths — and visible order rather than click/pin order — is what keeps
 * differently-sized columns contiguous and prevents overlap.
 */
function cumulativeOffset(
  columnId: string,
  pinnedSide: string[],
  side: 'left' | 'right',
  measuredWidths: Record<string, number> | undefined,
  columnWidths: Record<string, number>,
  columnOrder: string[] | undefined,
): number {
  const order =
    columnOrder && columnOrder.length > 0 ? columnOrder.filter((id) => pinnedSide.includes(id)) : pinnedSide;
  const index = order.indexOf(columnId);
  if (index < 0) return 0;
  const widthOf = (id: string) => measuredWidths?.[id] || columnWidths[id] || DEFAULT_PINNED_COLUMN_WIDTH;
  let offset = 0;
  if (side === 'left') {
    for (let i = 0; i < index; i++) offset += widthOf(order[i]);
  } else {
    for (let i = index + 1; i < order.length; i++) offset += widthOf(order[i]);
  }
  return offset;
}

/**
 * Computes the sticky positioning style for a table column cell driven by the
 * shared pinnedLeft / pinnedRight preference model. Used by both header and
 * body cells so their sticky offsets stay in sync.
 */
export function getPinnedColumnStyle(
  columnId: string,
  pinnedLeft: string[],
  pinnedRight: string[],
  columnWidths: Record<string, number>,
  options: PinnedColumnStyleOptions = {},
): CSSProperties {
  const { zIndex = 10, unpinnedPosition, measuredWidths, columnOrder } = options;
  const isPinLeft = pinnedLeft.includes(columnId);
  const isPinRight = pinnedRight.includes(columnId);
  const colWidth = columnWidths[columnId];
  const widthStyle: CSSProperties = colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px` } : {};

  if (!isPinLeft && !isPinRight) {
    return { ...widthStyle, position: unpinnedPosition };
  }

  const pinLeftOffset = cumulativeOffset(columnId, pinnedLeft, 'left', measuredWidths, columnWidths, columnOrder);
  const pinRightOffset = cumulativeOffset(columnId, pinnedRight, 'right', measuredWidths, columnWidths, columnOrder);

  return {
    ...widthStyle,
    position: 'sticky',
    left: isPinLeft ? `${pinLeftOffset}px` : undefined,
    right: isPinRight ? `${pinRightOffset}px` : undefined,
    zIndex,
  };
}

/**
 * Tracks the actual rendered width of each column by observing its header cell.
 * Attach `measureRef(columnId)` to the header `<th>`; the returned `widths` map
 * updates as columns render or are resized, so pinned offsets stay exact even
 * when columns have different (and non-persisted) widths.
 */
export function useColumnWidthObserver() {
  const [widths, setWidths] = useState<Record<string, number>>({});
  const elements = useRef<Map<string, HTMLElement>>(new Map());

  const observerRef = useRef<ResizeObserver | null>(null);
  if (observerRef.current === null && typeof ResizeObserver !== 'undefined') {
    observerRef.current = new ResizeObserver((entries) => {
      setWidths((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const entry of entries) {
          const target = entry.target as HTMLElement;
          const columnId = target.dataset.pinColId;
          if (!columnId) continue;
          // Border-box width (the column pitch) is what the next pinned column
          // must be offset by; contentRect would drop padding + border.
          const width = target.getBoundingClientRect().width;
          if (Math.abs((next[columnId] ?? 0) - width) > 0.5) {
            next[columnId] = width;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    });
  }

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const measureRef = useCallback(
    (columnId: string) => (el: HTMLElement | null) => {
      const observer = observerRef.current;
      const previous = elements.current.get(columnId);
      if (previous && previous !== el) observer?.unobserve(previous);
      if (el) {
        el.dataset.pinColId = columnId;
        elements.current.set(columnId, el);
        observer?.observe(el);
      } else {
        elements.current.delete(columnId);
      }
    },
    [],
  );

  return { widths, measureRef };
}
